import { createEvent, restore, sample } from 'effector'

import { fx } from '../../../libs'
import {
  MoneyHistory,
  MoneyHistoryRequest,
  MoneyHistoryResponse,
  MoneyHistoryStatus,
  MoneyHistoryStatusesResponse,
} from './types'
import { RequestError } from '../../../shared/errorResponse'

export const moneyHistoriesListMounted = createEvent<MoneyHistoryRequest>()

export const setMoneyHistoriesRequestError = createEvent<RequestError>()

export const getMoneyHistoriesFailed = createEvent<RequestError | null>()

export const getMoneyHistories = fx<MoneyHistoryRequest, MoneyHistoryResponse>({
  method: 'GET',
  url: `/money_history`,
})

const doctrineOrderField = (order: string): string => {
  const direction = order.startsWith('-') ? '-' : ''
  const field = direction ? order.slice(1) : order

  return `${direction}${
    field === 'humanUpdatedAt'
      ? 'updatedAt'
      : field === 'humanCreatedAt'
        ? 'createdAt'
        : field
  }`
}

export const getMoneyHistoryStatuses = fx<void, MoneyHistoryStatusesResponse>({
  method: 'GET',
  url: `/money_history/statuses`,
})

export const $moneyHistoryStatuses = restore<MoneyHistoryStatus[]>(
  getMoneyHistoryStatuses.doneData.map((a) => a.body),
  [],
)

export const $moneyHistories = restore<MoneyHistory[]>(
  getMoneyHistories.doneData.map((a) => a.body.items),
  [],
)

export const $totalCountOfMoneyHistories = restore(
  getMoneyHistories.doneData.map((a) => a.body.total),
  0,
)

export const $errorMoneyHistoriesRequest = restore<RequestError | null>(
  getMoneyHistoriesFailed,
  null,
).reset([moneyHistoriesListMounted])

sample({
  clock: moneyHistoriesListMounted,
  fn: ({ clientId, limit, page, order, moneyHistoryType, filters }) => {
    const query: Record<string, any> = {
      page: String(page),
      limit: String(limit),
      order: doctrineOrderField(order),
    }

    const where: Record<string, any> = { clientId }
    const {
      operationType,
      operationStatus,
      humanCreatedAt,
      humanUpdatedAt,
      ...searchFilters
    } = filters || {}

    // Приоритет: filters.operationType → moneyHistoryType === 'nova'
    if (operationType?.length) {
      where.operationType = operationType
    } else if (moneyHistoryType === 'nova') {
      where.operationType = [
        'nova_deposit',
        'nova_sale',
        'nova_withdrawal',
        'dividend_accrued',
        'dividend_withdrawal',
        'internal_resale_sell',
        'internal_resale_buy',
        'internal_sale',
      ]
    }

    if (operationStatus?.length) {
      where.operationStatus = operationStatus
    }

    query.where = JSON.stringify({
      ...where,
      ...searchFilters,
      ...(humanCreatedAt ? { createdAt: humanCreatedAt } : {}),
      ...(humanUpdatedAt ? { updatedAt: humanUpdatedAt } : {}),
    })

    return { query }
  },
  target: getMoneyHistories,
})

sample({
  clock: getMoneyHistories.failData,
  fn: (failData) => {
    return { message: failData.body.message, code: failData.status }
  },
  target: [setMoneyHistoriesRequestError],
})

getMoneyHistories.fail.watch(({ error }) =>
  getMoneyHistoriesFailed({ code: error.status, message: error.body.message }),
)

getMoneyHistories.done.watch(() => getMoneyHistoriesFailed(null))
